import React, { useEffect, useRef, useState } from 'react';
import { useContentStore } from '../../context/ContentContext';
import { IMAGE_LIBRARY } from '../../content/imageLibrary';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const shortName = (path) => {
    if (!path) return '';
    if (path.startsWith('data:')) return 'Uploaded image (prototype)';
    return path.split('/').pop();
};

/**
 * Editor for a single image value: preview, upload, pick from the library,
 * or type a path by hand.
 */
const ImageField = ({ label, value, onChange, extraImages = [] }) => {
    const { uploadImage } = useContentStore();
    const fileInputRef = useRef(null);
    const [showLibrary, setShowLibrary] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    // Close the picker with Escape
    useEffect(() => {
        if (!showLibrary) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setShowLibrary(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showLibrary]);

    // Files that ship with the site come first and win on name clashes, so the
    // picker always hands back the filename that actually exists on disk
    // (some pages reference "Photo.jpg" where the file is "Photo.JPG").
    const library = [];
    const seen = new Set();
    [...IMAGE_LIBRARY, ...extraImages].forEach(img => {
        if (!img) return;
        const key = img.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        library.push(img);
    });

    const handleFile = async (file) => {
        setUploadError('');

        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setUploadError('Please choose an image file (JPG, PNG, GIF or WebP).');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setUploadError('Image is too large. Maximum size is 5MB.');
            return;
        }

        setIsUploading(true);
        try {
            const url = await uploadImage(file);
            onChange(url);
        } catch (err) {
            setUploadError(err.message || 'Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div>
            {label && (
                <label className="block text-gray-300 text-sm font-medium mb-2">{label}</label>
            )}

            <div className="flex items-start gap-4 bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                {/* Preview */}
                <div className="w-28 h-20 flex-shrink-0 bg-gray-700 rounded overflow-hidden flex items-center justify-center">
                    {value ? (
                        <img src={value} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-gray-500 text-xs">No image</span>
                    )}
                </div>

                <div className="flex-grow min-w-0">
                    <p className="text-gray-300 text-sm truncate" title={value}>{shortName(value)}</p>

                    <div className="flex flex-wrap gap-2 mt-2">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                isUploading
                                    ? 'bg-gray-700 text-gray-400 cursor-wait'
                                    : 'bg-primary-red hover:bg-primary-dark-red text-white'
                            }`}
                        >
                            {isUploading ? 'Uploading...' : 'Upload new'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowLibrary(true)}
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs font-medium transition-colors"
                        >
                            Choose existing
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                                handleFile(e.target.files[0]);
                                e.target.value = '';
                            }}
                        />
                    </div>

                    <input
                        type="text"
                        value={value && value.startsWith('data:') ? '' : value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={value && value.startsWith('data:') ? 'Uploaded image - type a path to replace it' : '/images/example.jpg'}
                        className="w-full mt-2 px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-primary-red"
                    />

                    {uploadError && <p className="text-red-400 text-xs mt-2">{uploadError}</p>}
                </div>
            </div>

            {/* Library picker */}
            {showLibrary && (
                <div
                    className="fixed inset-0 z-50 bg-black/75 flex items-start justify-center p-4 pt-10 overflow-y-auto"
                    onClick={() => setShowLibrary(false)}
                >
                    <div
                        className="bg-gray-800 rounded-2xl w-full max-w-3xl border border-gray-700 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <h3 className="font-poppins font-semibold text-xl text-white">Choose an image</h3>
                            <button
                                type="button"
                                onClick={() => setShowLibrary(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[65vh] overflow-y-auto">
                            {library.map(img => (
                                <button
                                    key={img}
                                    type="button"
                                    onClick={() => { onChange(img); setShowLibrary(false); }}
                                    className={`group rounded-lg overflow-hidden border-2 transition-colors ${
                                        img === value ? 'border-primary-red' : 'border-transparent hover:border-gray-500'
                                    }`}
                                >
                                    <div className="h-24 bg-gray-700">
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </div>
                                    <p className="text-gray-400 text-[11px] p-1.5 truncate group-hover:text-white">
                                        {shortName(img)}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageField;
